import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Calendar, MapPin, Package, X } from "lucide-react"
import { Button } from "../components/ui/button.tsx"
import { ProfileLayout } from "../components/ProfileLayout"
import { Pagination } from "../components/Pagination"
import api from "../utils/api"
import { getCookie } from "../utils/cookies"
import { format } from "date-fns"

function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function formatDate(dateStr) {
  if (!dateStr) return ""
  // Handle DD-MM-YYYY format
  const parts = dateStr.split("-")
  if (parts.length === 3 && parts[0].length <= 2) {
    return `${parts[0]} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(parts[1], 10) - 1]} ${parts[2]}`
  }
  const d = new Date(dateStr)
  if (isNaN(d)) return dateStr
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

export default function MyPackagesPage() {
  const navigate = useNavigate()
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 10

  // Cancel modal
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelPkg, setCancelPkg] = useState(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelReason, setCancelReason] = useState("")

  // Reschedule modal
  const [showReschedule, setShowReschedule] = useState(false)
  const [reschedulePkg, setReschedulePkg] = useState(null)
  const [rescheduleDate, setRescheduleDate] = useState("")
  const [rescheduleReason, setRescheduleReason] = useState("")
  const [rescheduleLoading, setRescheduleLoading] = useState(false)

  const todayStr = format(new Date(), "yyyy-MM-dd")

  const fetchPackages = async () => {
    try {
      const userStr = getCookie("user")
      if (!userStr || userStr === "undefined") { navigate("/"); return }
      const user = JSON.parse(userStr)
      let mobile = user?.mobile || user?.phone || ""
      if (!mobile) return
      if (!mobile.startsWith("91")) mobile = `91${mobile}`
      const response = await api.get(`/apis/web/bookingpackages?mobile=${mobile}`)
      if (response.data?.success && Array.isArray(response.data.data)) {
        setPackages(response.data.data.map((item) => ({
          id: item.indentNo,
          patientName: toTitleCase(item.patient?.name || ""),
          mrNo: item.patient?.mrNo || "",
          mobile: item.patient?.mobile || "",
          packageName: item.services?.map(s => toTitleCase(s.servName)).join(", ") || "",
          date: formatDate(item.indentDate),
          rawDate: item.indentDate || "",
          isPast: (() => {
            const parts = (item.indentDate || "").split("-")
            if (parts.length === 3 && parts[0].length <= 2) {
              const d = new Date(parts[2], parseInt(parts[1], 10) - 1, parseInt(parts[0], 10))
              return d < new Date(new Date().toDateString())
            }
            return false
          })(),
          location: item.hospitalName || "",
          hospitalId: item.hospitalId || "",
          amount: item.services?.reduce((sum, s) => sum + (s.serviceCost || 0), 0) || 0,
          paymentStatus: item.paymentStatus || "",
          indentStatus: item.indentStatus || "",
          bookingStatus: item.bookingStatus || "",
        })))
      }
    } catch (error) {
      console.error("Error fetching packages:", error)
    }
  }

  useEffect(() => {
    fetchPackages().finally(() => setLoading(false))
  }, [navigate])

  const openReschedule = (pkg) => {
    setReschedulePkg(pkg)
    setRescheduleDate("")
    setRescheduleReason("")
    setShowReschedule(true)
  }

  const handleRescheduleConfirm = async () => {
    if (!rescheduleDate || !reschedulePkg) return
    setRescheduleLoading(true)
    try {
      const res = await api.post("/apis/web/reschedulepackage", {
        indentNo: reschedulePkg.id,
        userId: reschedulePkg.mrNo,
        newDate: rescheduleDate,
      })
      if (res.data?.success || res.data?.status === 0) {
        await fetchPackages()
        setShowReschedule(false)
        alert("Package rescheduled successfully!")
      } else {
        alert(res.data?.message || "Failed to reschedule package")
      }
    } catch {
      alert("Failed to reschedule package")
    }
    setRescheduleLoading(false)
  }

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Package className="w-6 h-6 text-red-600" />
              <h1 className="text-2xl font-bold text-gray-900">My Packages</h1>
            </div>
            <Button className="gap-2 w-full sm:w-auto" onClick={() => navigate("/book-now")}>
              <Calendar className="w-4 h-4" />
              Book an Appointment
            </Button>
          </div>
        </div>

        <div className="px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading your packages...</p>
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-12 text-gray-500 font-medium">No packages found.</div>
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {packages.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((pkg) => (
              <div key={pkg.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{pkg.patientName}</h3>
                      <p className="text-sm text-gray-600">{pkg.mrNo}</p>
                      <p className="text-sm text-gray-600 font-medium">{pkg.packageName}</p>
                    </div>
                  </div>
                  {pkg.bookingStatus === "C" && (
                    <span className="px-3 py-1 rounded-md text-sm font-medium border bg-red-50 text-red-700 border-red-200">
                      Cancelled
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{pkg.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{pkg.location}</span>
                    {pkg.amount > 0 && <span className="ml-auto font-medium text-gray-900">₹{pkg.amount}</span>}
                  </div>
                </div>

                {pkg.bookingStatus !== "C" && !pkg.isPast && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => openReschedule(pkg)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition-colors"
                    >
                      Reschedule
                    </button>
                    <button
                      onClick={() => { setCancelPkg(pkg); setCancelReason(""); setShowCancelModal(true) }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-600 hover:text-white transition-colors"
                    >
                      Cancel Package
                    </button>
                  </div>
                )}
              </div>
            ))}
            </div>
            <Pagination currentPage={currentPage} totalPages={Math.ceil(packages.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white rounded-xl w-[90vw] max-w-[380px] p-6 shadow-xl" style={{ zIndex: 51 }}>
            <div className="text-center">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Package</h3>
              <p className="text-sm text-gray-600 mb-4">Are you sure you want to cancel this package?</p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none mb-6"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  No
                </button>
                <button
                  disabled={cancelLoading || !cancelReason.trim()}
                  onClick={async () => {
                    setCancelLoading(true)
                    try {
                      const res = await api.post("/apis/web/cancelpackage", {
                        indentNo: cancelPkg.id,
                        cancelledBy: cancelPkg.mrNo,
                        Remarks: cancelReason,
                      })
                      if (res.data?.success || res.data?.status === 0) {
                        await fetchPackages()
                        setShowCancelModal(false)
                        setTimeout(() => alert("Package cancelled. Refund within 5-7 working days."), 100)
                      } else {
                        alert(res.data?.message || "Failed to cancel package")
                      }
                    } catch {
                      alert("Failed to cancel package")
                    }
                    setCancelLoading(false)
                  }}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cancelLoading ? "Cancelling..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showReschedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowReschedule(false)} />
          <div className="relative bg-white rounded-xl w-[95vw] max-w-[500px] max-h-[85vh] overflow-y-auto shadow-xl" style={{ zIndex: 51 }}>
            <button
              onClick={() => setShowReschedule(false)}
              className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-5 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 pr-8">Reschedule Package</h2>
              <p className="text-sm text-gray-600 mb-1">{reschedulePkg?.packageName}</p>
              <p className="text-xs text-gray-500 mb-6">{reschedulePkg?.patientName} &middot; {reschedulePkg?.location}</p>

              {/* Date Picker */}
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Select Appointment Date</h3>
              <input
                type="date"
                value={rescheduleDate}
                min={todayStr}
                onChange={(e) => setRescheduleDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary mb-4"
              />

              {/* Reason */}
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Enter reason for rescheduling</h3>
              <textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Enter reason for rescheduling..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-4"
              />

              {/* Confirm Button */}
              <button
                onClick={handleRescheduleConfirm}
                disabled={!rescheduleDate || rescheduleLoading}
                className="w-full mt-2 px-4 py-3 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rescheduleLoading ? "Rescheduling..." : "Confirm Reschedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ProfileLayout>
  )
}
